import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { editionTitle } from '../../changelogAdminConfig';

const pad = (n) => String(n).padStart(2, '0');
const warningText = (warning) => (typeof warning === 'string' ? warning : warning?.message || String(warning));

// Valor inicial: o agendamento atual ou amanhã às 09:00
const initialValue = (edition) => {
  if (edition?.scheduled_at) {
    const [date, time = '09:00'] = String(edition.scheduled_at).split(/[ T]/);
    return { date, time: time.slice(0, 5) };
  }
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { date: `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`, time: '09:00' };
};

/**
 * Data e hora do envio. Quando a API devolve avisos (ex.: outro envio há menos de 3 dias),
 * eles aparecem aqui e o botão vira "Agendar mesmo assim".
 * @param {{ edition: Object|null, warnings: string[], error: string|null, busy: boolean,
 *   onClose: Function, onSubmit: Function }} props - onSubmit("AAAA-MM-DD HH:MM", confirm)
 */
function ScheduleDialog({ edition, warnings, error, busy, onClose, onSubmit }) {
  const [value, setValue] = useState(() => initialValue(edition));

  // Os avisos valem para a data em que foram dados; mudou a data, a API confere de novo
  const [warnedValue, setWarnedValue] = useState(null);

  useEffect(() => { if (edition) setValue(initialValue(edition)); }, [edition]);
  useEffect(() => { setWarnedValue(warnings.length > 0 ? value : null); }, [warnings]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsConfirm = warnings.length > 0 && warnedValue !== null
    && warnedValue.date === value.date && warnedValue.time === value.time;
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(value.date) && /^\d{2}:\d{2}$/.test(value.time);
  const change = (key) => (e) => setValue((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <Dialog open={Boolean(edition)} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">{editionTitle(edition)}</Typography>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>Agendar envio</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField size="small" type="date" label="Dia" InputLabelProps={{ shrink: true }} value={value.date} onChange={change('date')} sx={{ flex: 1 }} />
          <TextField size="small" type="time" label="Hora" InputLabelProps={{ shrink: true }} value={value.time} onChange={change('time')} sx={{ width: 120 }} />
        </Box>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Sai em até uma hora depois do horário marcado. Até lá dá para editar ou desagendar.
        </Typography>
        {needsConfirm && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {warnings.map((warning, index) => <li key={index}>{warningText(warning)}</li>)}
            </Box>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button
          variant="contained"
          color={needsConfirm ? 'warning' : 'primary'}
          disabled={!valid || busy}
          onClick={() => onSubmit(`${value.date} ${value.time}`, needsConfirm)}
        >
          {busy ? 'Agendando...' : needsConfirm ? 'Agendar mesmo assim' : 'Agendar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleDialog;
